import { ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ClipboardEvent } from "react";
import { defaultPhoneCountryCode, getPhoneCountry, normalizeNationalNumber, normalizePhoneValue, parseInternationalPaste, phoneCountries, type PhoneCountry } from "../../shared/phone-codes";

type Props = {
  name: string;
  defaultCountryCode?: string;
  defaultNationalNumber?: string;
  label?: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
};

function countryMatches(country: PhoneCountry, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return country.code.toLowerCase().includes(normalized) || country.name.toLowerCase().includes(normalized) || country.dialCode.includes(normalized.replace(/[^0-9]/g, ""));
}

export function PhoneNumberField({ name, defaultCountryCode = defaultPhoneCountryCode, defaultNationalNumber = "", label = "Phone number", placeholder = "", onValueChange }: Props) {
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [nationalNumber, setNationalNumber] = useState(() => normalizeNationalNumber(defaultNationalNumber));
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [touchedCountry, setTouchedCountry] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!touchedCountry) setCountryCode(defaultCountryCode);
  }, [defaultCountryCode, touchedCountry]);

  useEffect(() => {
    const normalized = normalizePhoneValue(countryCode, nationalNumber);
    onValueChange?.(normalized);
  }, [countryCode, nationalNumber, onValueChange]);

  useEffect(() => {
    if (!menuOpen) return;
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [menuOpen]);

  const country = getPhoneCountry(countryCode);
  const value = useMemo(() => normalizePhoneValue(countryCode, nationalNumber), [countryCode, nationalNumber]);
  const filteredCountries = useMemo(() => phoneCountries.filter((item) => countryMatches(item, query)), [query]);

  const chooseCountry = (nextCountry: PhoneCountry) => {
    setTouchedCountry(true);
    setCountryCode(nextCountry.code);
    setNationalNumber((current) => normalizeNationalNumber(current));
    setMenuOpen(false);
    setQuery("");
    window.setTimeout(() => buttonRef.current?.focus(), 0);
  };

  const onNumberChange = (nextValue: string) => {
    setNationalNumber(normalizeNationalNumber(nextValue));
  };

  const onNumberPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;
    event.preventDefault();
    const parsed = parseInternationalPaste(pasted);
    if (parsed.countryCode) {
      setTouchedCountry(true);
      setCountryCode(parsed.countryCode);
    }
    setNationalNumber(parsed.nationalNumber);
  };

  return (
    <div className="phone-field">
      <span className="phone-label">{label}</span>
      <div className="phone-input-row">
        <div className="country-code-picker">
          <button
            ref={buttonRef}
            type="button"
            className="country-code-button"
            onClick={() => setMenuOpen((current) => !current)}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            aria-label={`Country code ${country.name} +${country.dialCode}`}
          >
            <span>+{country.dialCode}</span>
            <ChevronDown size={15} />
          </button>
          {menuOpen && (
            <div className="country-code-menu" role="dialog" aria-label="Choose a country code">
              <label className="country-code-search">
                <Search size={14} />
                <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search country or code" />
              </label>
              <div className="country-code-list" role="listbox">
                {filteredCountries.map((item) => (
                  <button key={item.code} type="button" role="option" className={item.code === country.code ? "selected" : ""} onClick={() => chooseCountry(item)}>
                    <span>{item.name}</span>
                    <strong>+{item.dialCode}</strong>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <input
          type="tel"
          autoComplete="tel-national"
          inputMode="tel"
          value={nationalNumber}
          placeholder={placeholder}
          aria-label={label}
          onChange={(event) => onNumberChange(event.target.value)}
          onPaste={onNumberPaste}
          onBlur={() => setNationalNumber((current) => normalizeNationalNumber(current))}
        />
      </div>
      <input type="hidden" name={name} value={value} />
    </div>
  );
}
